import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';

import useResettingState from 'hooks/useResettingState.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useThemeWithBright } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import focusElement from 'utils/dom/focusElement.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import deRef from 'utils/objects/deRef.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import asInput from 'utils/strings/asInput.util';

import CN from './Note.module.scss';

const debouncedCall = debounce(call, 1000, { leading: false, trailing: true, maxWait: 1000 });
const fromRefToSelectionInfo = (ref) => ({ end: ref?.current?.selectionEnd, bgn: ref?.current?.selectionStart });

const Note = ({
  autofocus,
  autoscroll,
  disabled,
  debounced,
  hasExternalValue,
  variantInvert,
  name: nameFromProps,
  onChange = alwaysNoop,
  onSelectionChange = alwaysNoop,
  tag: tagFromProps,
  value: valueFromProps,
  variant,
  height,
  placeholder,
  empty, // without background image
}) => {
  const raise = debounced ? debouncedCall : call;

  const { isDarkMode } = useThemeWithBright();
  const { t } = useTranslation();

  const name = fromAnyToTrimmed(nameFromProps);
  const tag = fromAnyToTrimmed(tagFromProps);

  const [prevBgn, setPrevBgn] = useStateWhileMounted();
  const [prevEnd, setPrevEnd] = useStateWhileMounted();

  const [localValue, setLocalValue] = useResettingState(fromAnyToString(valueFromProps), hasExternalValue ? useResettingState.ALWAYS : useResettingState.NIL);

  const areaRef = useRef(null);

  const raiseSelectionChange = useCallback(
    (source, ev) => {
      const raw = fromRefToSelectionInfo(areaRef);

      // NOTE: don't spam parent needlessly, just upon actual change
      if (raw.bgn === prevBgn && raw.end === prevEnd) return;

      setPrevBgn(raw.bgn);
      setPrevEnd(raw.end);
      raw.source = source;

      // noinspection JSCheckFunctionSignatures
      raise(`Note.onSelectionChange`, onSelectionChange, { name, tag, raw, ev });
    },
    [prevBgn, prevEnd, setPrevBgn, setPrevEnd, raise, onSelectionChange, name, tag],
  );

  const handleClick = useCallback(
    //
    (ev) => {
      if (autoscroll) elementScrollTo({ element: deRef(areaRef), block: 'center' });

      raiseSelectionChange('click', ev);
    },
    [autoscroll, raiseSelectionChange],
  );

  const handleKeyDown = useCallback((ev) => raiseSelectionChange('keydn', ev), [raiseSelectionChange]);
  const handleKeyUp = useCallback((ev) => raiseSelectionChange('keyup', ev), [raiseSelectionChange]);
  const handleSelect = useCallback((ev) => raiseSelectionChange('select', ev), [raiseSelectionChange]);

  // TODO: if more functionality is required, make it almost the same as TextInput, like TextAreaInput
  const handleChange = useCallback(
    (ev) => {
      raiseSelectionChange('change', ev);

      const raw = fromEventToValue(ev);

      const result = raise(`Note.onChange`, onChange, { name, tag, raw, ev });
      // TODO: can raw be undefined or null? does it need more or less checks here?
      setLocalValue(isMissing(result?.value) ? (isMissing(raw) ? '' : raw) : result?.value);
    },
    [raise, name, onChange, raiseSelectionChange, setLocalValue, tag],
  );

  const { bgn: nextBgn, end: nextEnd } = fromRefToSelectionInfo(areaRef);

  useEffect(() => {
    if (autofocus) focusElement(deRef(areaRef));
    // NOTE: use the next (not previous) values as dependencies to raise event every time they change
    // noinspection JSCheckFunctionSignatures
    raiseSelectionChange('effect');
  }, [autofocus, nextBgn, nextEnd, raiseSelectionChange]);

  // NOTE: height adjustment logic only when the variant of the Note is set to "fullHeight"
  useEffect(() => {
    if (variant === 'fullHeight' && areaRef.current) {
      areaRef.current.style.height = `${areaRef.current.scrollHeight}px`;
    }
  }, [variant, localValue]);

  return (
    <div
      data-bem="Note"
      data-name={name}
      data-tag={tag}
      className={cn({
        [CN.component]: true,
        [CN[`${variant}Variant`]]: variant,
        [CN[`${height}Height`]]: height,
        [CN.empty]: empty,
        [CN.variantInvert]: variantInvert,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <textarea
        data-bem="Note__input"
        data-name={name}
        data-tag={tag}
        ref={areaRef}
        className={CN.note}
        disabled={disabled}
        name={name}
        value={asInput(localValue)}
        onClick={handleClick}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onSelect={handleSelect}
        placeholder={placeholder || t('composite.note.leaveNote')}
      />
    </div>
  );
};

export default Note;
